import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useSetError } from '../../../../redux/modals';
import { isInternalSelector } from '../../../../redux/user';
import { getCompanySlug } from '../../../../utils/hooks/useLocationCompany';
import { DEFAULT_ATTRIBUTION, DEFAULT_DATE_RANGE } from '../dtc-commerce-mmm-constants';
import { buildExportFilename as buildExportFilenameWithPrefix } from '../utils/dtc-commerce-mmm-export';
import { DTC_DASHBOARD_CONFIG, MmmDashboardConfig } from '../dtc-commerce-mmm-variants';
import {
  AttributionType,
  DashboardData,
  DateRange,
  DateRangePresetConst
} from '../dtc-commerce-mmm-types';
import { resolveChannelColor } from '../utils/dtc-commerce-mmm-channel-colors';
import { LoadContext, loadDtcCommerceMmmData } from '../utils/dtc-commerce-mmm-data-loader';
import {
  discoverMmmRuns,
  fixedRunDiscovery,
  MmmDiscovery,
  MmmKpi
} from '../utils/dtc-commerce-mmm-runs-discovery';

interface DtcCommerceMmmDataContextValue {
  config: MmmDashboardConfig;
  // Prefixed for the current variant; call sites pass only client/visual/channel.
  buildExportFilename: (client: string, visual: string, channelSlug?: string) => string;
  data: DashboardData | null;
  loading: boolean;
  isInternal: boolean;
  attributionType: AttributionType;
  setAttributionType: (type: AttributionType) => void;
  dateRange: DateRange;
  setDateRange: (range: DateRange) => void;
  discovery: MmmDiscovery | null;
  selectedKpi: string | null;
  setSelectedKpi: (kpi: string) => void;
  selectedTimestamp: string | null;
  setSelectedTimestamp: (timestamp: string) => void;
  selectRun: (kpi: string, timestamp: string) => void;
  // The loaded run's coordinates, for data a panel loads on its own.
  runContext: LoadContext | null;
}

const DtcCommerceMmmDataContext = createContext<DtcCommerceMmmDataContextValue>({
  config: DTC_DASHBOARD_CONFIG,
  buildExportFilename: () => '',
  data: null,
  loading: true,
  isInternal: false,
  attributionType: DEFAULT_ATTRIBUTION,
  setAttributionType: () => {},
  dateRange: DEFAULT_DATE_RANGE,
  setDateRange: () => {},
  discovery: null,
  selectedKpi: null,
  setSelectedKpi: () => {},
  selectedTimestamp: null,
  setSelectedTimestamp: () => {},
  selectRun: () => {},
  runContext: null
});

// Runs are already newest-first, so this lands on the newest released run — falling back to the
// newest run overall. Without the preference an internal user would always open on an unreleased
// run, which is not the one they mean to look at.
const defaultRunTimestamp = (kpi: MmmKpi): string | null =>
  (kpi.runs.find(r => r.clientFacing) ?? kpi.runs[0])?.timestamp ?? null;

interface DtcCommerceMmmDataProviderProps extends PropsWithChildren {
  config: MmmDashboardConfig;
}

export const DtcCommerceMmmDataProvider = ({
  config,
  children
}: DtcCommerceMmmDataProviderProps) => {
  const { pathname } = useLocation();
  const client = getCompanySlug(pathname);
  const isInternal = useSelector(isInternalSelector);

  const setError = useSetError();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [attributionType, setAttributionType] = useState<AttributionType>(DEFAULT_ATTRIBUTION);
  const [dateRange, setDateRange] = useState<DateRange>(DEFAULT_DATE_RANGE);
  const [discovery, setDiscovery] = useState<MmmDiscovery | null>(null);
  const [selectedKpi, setSelectedKpi] = useState<string | null>(null);
  const [selectedTimestamp, setSelectedTimestamp] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setData(null);
    setDiscovery(null);
    setSelectedKpi(null);
    setSelectedTimestamp(null);

    const runs = config.fixedRun
      ? Promise.resolve(fixedRunDiscovery(config.fixedRun, client, isInternal))
      : discoverMmmRuns(client, isInternal, config.modelType);

    runs
      .then(result => {
        if (cancelled) return;
        setDiscovery(result);
        // No runs is an empty state (handled inline by the page), not an error.
        if (result.kpis.length === 0) {
          setLoading(false);
          return;
        }
        const firstKpi = result.kpis[0];
        setSelectedKpi(firstKpi.name);
        setSelectedTimestamp(defaultRunTimestamp(firstKpi));
      })
      .catch(e => {
        if (cancelled) return;
        setLoading(false);
        setError({
          message: e.message,
          reportError: e
        });
      });

    return () => {
      cancelled = true;
    };
  }, [client, isInternal, config.modelType, config.fixedRun, setError]);

  // The selected run's coordinates. The run's branch and output file version come off the
  // discovery row, so this stays null until the selection and the discovery result agree. They
  // disagree for one render after a KPI switch.
  const runContext = useMemo<LoadContext | null>(() => {
    if (!client || !selectedKpi || !selectedTimestamp) return null;
    const selectedRun = discovery?.kpis
      .find(k => k.name === selectedKpi)
      ?.runs.find(r => r.timestamp === selectedTimestamp);
    if (!selectedRun) return null;
    return {
      client,
      branch: selectedRun.branch,
      kpi: selectedKpi,
      timestamp: selectedTimestamp,
      outputFileVersion: selectedRun.outputFileVersion
    };
  }, [client, selectedKpi, selectedTimestamp, discovery]);

  useEffect(() => {
    if (!runContext) return undefined;

    let cancelled = false;
    setLoading(true);
    setData(null);

    loadDtcCommerceMmmData(runContext)
      .then(d => {
        if (cancelled) return;
        setData(d);
        // Use Date comparison rather than string comparison to avoid any edge-case
        // collation issues. t12mStartDate > dataStartDate means the dataset is longer
        // than 52 weeks so the T12M window fits entirely within the data.
        const hasFullYear =
          new Date(`${d.t12mStartDate}T00:00:00`) > new Date(`${d.dataStartDate}T00:00:00`);
        setDateRange(
          hasFullYear
            ? {
                preset: DateRangePresetConst.T12M,
                startDate: d.t12mStartDate,
                endDate: d.t12mEndDate,
                label: 'Trailing 12 months'
              }
            : {
                preset: DateRangePresetConst.ALL,
                startDate: d.dataStartDate,
                endDate: d.dataEndDate,
                label: 'All time'
              }
        );
        setLoading(false);
      })
      .catch(e => {
        if (cancelled) return;
        setLoading(false);
        setError({
          message: e.message,
          reportError: e
        });
      });

    return () => {
      cancelled = true;
    };
  }, [runContext, setError]);

  const handleSelectKpi = useCallback(
    (kpi: string) => {
      setSelectedKpi(kpi);
      const nextKpi = discovery?.kpis.find(k => k.name === kpi);
      setSelectedTimestamp(nextKpi ? defaultRunTimestamp(nextKpi) : null);
    },
    [discovery]
  );

  // `clientName` is the model's client label (modelInfo.client), not the company slug
  // this provider loads by — hence not reusing the `client` name from the outer scope.
  const buildExportFilename = useCallback(
    (clientName: string, visual: string, channelSlug?: string) =>
      buildExportFilenameWithPrefix(config.exportPrefix, clientName, visual, channelSlug),
    [config.exportPrefix]
  );

  const selectRun = useCallback((kpi: string, timestamp: string) => {
    setSelectedKpi(kpi);
    setSelectedTimestamp(timestamp);
  }, []);

  const value = useMemo<DtcCommerceMmmDataContextValue>(
    () => ({
      config,
      buildExportFilename,
      data,
      loading,
      isInternal,
      attributionType,
      setAttributionType,
      dateRange,
      setDateRange,
      discovery,
      selectedKpi,
      setSelectedKpi: handleSelectKpi,
      selectedTimestamp,
      setSelectedTimestamp,
      selectRun,
      runContext
    }),
    [
      config,
      buildExportFilename,
      data,
      loading,
      isInternal,
      attributionType,
      dateRange,
      discovery,
      selectedKpi,
      selectedTimestamp,
      handleSelectKpi,
      selectRun,
      runContext
    ]
  );

  return (
    <DtcCommerceMmmDataContext.Provider value={value}>
      {children}
    </DtcCommerceMmmDataContext.Provider>
  );
};

export const useDtcCommerceMmmData = (): DtcCommerceMmmDataContextValue =>
  useContext(DtcCommerceMmmDataContext);

// Channel → color for the loaded run, so every chart on the page agrees and no two channels
// share a color. Platforms and features take their channel's color.
export const useChannelColor = (): ((channel: string) => string) => {
  const { data } = useDtcCommerceMmmData();
  const runColors = data?.channelColors;
  return useCallback((channel: string) => resolveChannelColor(channel, runColors), [runColors]);
};
